import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Package, Plus, Search, Edit, Trash2, Image, 
  Truck, Boxes, Wrench, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsServicesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('produit');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'agglo',
    type: 'produit',
    description: '',
    unit: 'unité',
    unit_price: '',
    photo_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/products-services`, getAuthHeaders());
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: activeTab === 'produit' ? 'agglo' : 'transport',
      type: activeTab,
      description: '',
      unit: 'unité',
      unit_price: '',
      photo_url: '',
      is_active: true
    });
    setEditingItem(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      type: item.type,
      description: item.description || '',
      unit: item.unit,
      unit_price: item.unit_price.toString(),
      photo_url: item.photo_url || '',
      is_active: item.is_active
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
      toast.error('Le prix est obligatoire');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        unit_price: parseFloat(formData.unit_price)
      };

      if (editingItem) {
        await axios.put(`${API}/products-services/${editingItem.id}`, data, getAuthHeaders());
        toast.success('Élément modifié avec succès');
      } else {
        await axios.post(`${API}/products-services`, data, getAuthHeaders());
        toast.success('Élément créé avec succès');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API}/products-services/${itemToDelete.id}`, getAuthHeaders());
      toast.success('Élément supprimé');
      setItemToDelete(null);
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      agglo: 'Agglos',
      granulat: 'Granulats',
      ciment: 'Ciment',
      transport: 'Transport',
      manutention: 'Manutention',
      location: 'Location',
      autre: 'Autre'
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'agglo':
      case 'granulat':
      case 'ciment':
        return <Boxes className="w-5 h-5" />;
      case 'transport':
        return <Truck className="w-5 h-5" />;
      case 'manutention':
      case 'location':
        return <Wrench className="w-5 h-5" />;
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      agglo: 'bg-blue-100 text-blue-800',
      granulat: 'bg-amber-100 text-amber-800',
      ciment: 'bg-gray-100 text-gray-800',
      transport: 'bg-green-100 text-green-800',
      manutention: 'bg-purple-100 text-purple-800',
      location: 'bg-orange-100 text-orange-800',
      autre: 'bg-slate-100 text-slate-800'
    };
    return colors[category] || colors.autre;
  };

  const filteredItems = items.filter(item => 
    item.type === activeTab &&
    (item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     item.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const productCategories = ['agglo', 'granulat', 'ciment', 'autre'];
  const serviceCategories = ['transport', 'manutention', 'location', 'autre'];

  // Stats
  const products = items.filter(i => i.type === 'produit');
  const services = items.filter(i => i.type === 'service');

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="products-services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3">
            <Package className="w-10 h-10" />
            Produits & Services
          </h1>
          <p className="text-sm text-slate-600 mt-1">Gérez votre catalogue de produits et services</p>
        </div>
        <Button
          onClick={openAddModal}
          data-testid="add-item-btn"
          className="bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm shadow-lg h-12 px-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau {activeTab === 'produit' ? 'Produit' : 'Service'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Boxes className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Produits</div>
              <div className="text-2xl font-bold">{products.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Services</div>
              <div className="text-2xl font-bold">{services.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Tag className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Agglos</div>
              <div className="text-2xl font-bold">{items.filter(i => i.category === 'agglo').length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Wrench className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Actifs</div>
              <div className="text-2xl font-bold">{items.filter(i => i.is_active).length}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="produit" className="flex items-center gap-2">
            <Boxes className="w-4 h-4" />
            Produits ({products.length})
          </TabsTrigger>
          <TabsTrigger value="service" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Services ({services.length})
          </TabsTrigger>
        </TabsList>

        {/* Search */}
        <Card className="p-4 mb-4 bg-white border border-slate-200 rounded-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Rechercher par nom ou catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </Card>

        {/* Grid of items */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card key={item.id} className={`bg-white border rounded-sm overflow-hidden ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`item-card-${item.id}`}>
              {/* Photo */}
              <div className="h-40 bg-slate-100 flex items-center justify-center relative">
                {item.photo_url ? (
                  <img 
                    src={item.photo_url} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-slate-300">
                    {getCategoryIcon(item.category)}
                    <span className="block text-xs mt-1">Pas de photo</span>
                  </div>
                )}
                <Badge className={`absolute top-2 left-2 ${getCategoryColor(item.category)}`}>
                  {getCategoryLabel(item.category)}
                </Badge>
                {!item.is_active && (
                  <Badge className="absolute top-2 right-2 bg-red-100 text-red-800">
                    Inactif
                  </Badge>
                )}
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className="font-bold text-[#1B3268] text-lg truncate">{item.name}</h3>
                {item.description && (
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-[#1B3268]">
                      {item.unit_price.toLocaleString('fr-FR')}
                    </span>
                    <span className="text-sm text-slate-500 ml-1">FCFA/{item.unit}</span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="mt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditModal(item)}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setItemToDelete(item)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <Card className="p-12 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Aucun {activeTab === 'produit' ? 'produit' : 'service'} trouvé</p>
            <Button onClick={openAddModal} className="mt-4" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un {activeTab === 'produit' ? 'produit' : 'service'}
            </Button>
          </Card>
        )}
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              {editingItem ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
              {editingItem ? 'Modifier' : 'Nouveau'} {formData.type === 'produit' ? 'Produit' : 'Service'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({
                    ...formData, 
                    type: v,
                    category: v === 'produit' ? 'agglo' : 'transport'
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="produit">Produit</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(formData.type === 'produit' ? productCategories : serviceCategories).map(cat => (
                      <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                placeholder="Ex: Agglo de 15, Transport 10T..."
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Description du produit ou service..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
              />
            </div>

            {/* Price & Unit */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix unitaire (FCFA) *</Label>
                <Input
                  type="number"
                  placeholder="Ex: 350"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Unité</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unité">Unité</SelectItem>
                    <SelectItem value="tonne">Tonne</SelectItem>
                    <SelectItem value="m³">M³</SelectItem>
                    <SelectItem value="voyage">Voyage</SelectItem>
                    <SelectItem value="jour">Jour</SelectItem>
                    <SelectItem value="heure">Heure</SelectItem>
                    <SelectItem value="forfait">Forfait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Photo URL */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                URL de la photo
              </Label>
              <Input
                placeholder="https://exemple.com/photo.jpg"
                value={formData.photo_url}
                onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
              />
              {formData.photo_url && (
                <div className="mt-2 h-32 rounded-md overflow-hidden bg-slate-100">
                  <img 
                    src={formData.photo_url} 
                    alt="Aperçu"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="is_active">Actif (visible dans les ventes)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              {saving ? 'Enregistrement...' : (editingItem ? 'Enregistrer' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer cet élément ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && (
                <div className="space-y-2 mt-2">
                  <p>Voulez-vous supprimer <strong>{itemToDelete.name}</strong> ?</p>
                  <p className="text-amber-600">⚠️ Cette action est irréversible.</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsServicesPage;
